import React from "react";
import { Tooltip } from "antd";

/**
 * TruncatedText Component - Reusable component for displaying truncated text with tooltips
 *
 * @param {Object} props - Component props
 * @param {string} props.text - The text to display
 * @param {number} props.maxLength - Maximum length before truncation (default: 6)
 * @param {boolean} props.bold - Whether to render text in bold (default: false)
 * @param {string} props.fallback - Text to show when input is empty/null (default: "-")
 * @param {string} props.tooltipTitle - Custom tooltip title (defaults to full text)
 * @param {boolean} props.showTooltip - Whether to show tooltip (default: true)
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} Rendered component
 */
const TruncatedText = ({
  text,
  maxLength = 6,
  bold = false,
  fallback = "-",
  tooltipTitle,
  showTooltip = true,
  className = "",
}) => {
  // Handle empty/null text
  if (!text) {
    return <span className={className}>{fallback}</span>;
  }

  // Check if truncation is needed
  const needsTruncation = text.length > maxLength;

  // Generate truncated text
  const truncated = needsTruncation
    ? `${text.substring(0, 3)}...${text.substring(text.length - 3)}`
    : text;

  // Determine tooltip content
  const tooltipContent = tooltipTitle || text;

  // Render with or without tooltip
  const content = bold ? (
    <strong>{truncated}</strong>
  ) : (
    <span>{truncated}</span>
  );

  if (showTooltip) {
    return (
      <Tooltip title={tooltipContent} placement="top" mouseEnterDelay={0.5}>
        <span className={className} style={{ cursor: "help" }}>
          {content}
        </span>
      </Tooltip>
    );
  }

  return <span className={className}>{content}</span>;
};

export default TruncatedText;
